import {Rank, Tensor} from "@tensorflow/tfjs";
import {ScanDetection, ScanFrame, ScanResult} from "alpine";

const frames: ScanFrame[] = [];

type Models = 'beersport' | 'beersport-budvar';
const ActualModel: Models = 'beersport-budvar';

const modelPromise = (async () => {
    const response = await fetch(ActualModel+ '/model.tflite');
    return await response.arrayBuffer();
})();

const loadModel = async () => {
    await window.tf.ready();
    return await window.tflite.loadTFLiteModel(await modelPromise);
}

const loadLabels = async () => {
    const response = await fetch(ActualModel+ '/labels.txt');
    return (await response.text()).split('\n').map(line => line.trim());
}

const callWithTimeout = async (fn: Function, timeoutMs = 30000) => {
    const timeout = new Promise((resolve) => {
        setTimeout(() => resolve(false), timeoutMs);
    });

    try {
        return Promise.race([fn(), timeout]);
    } catch (e) {
        console.error('Funkce selhala:', e);
        return false;
    }
}

const detectObjects = async (
    video: HTMLVideoElement,
    requiredLabel: string,
    requiredProbability: number,
): Promise<ScanResult> => {

    const model = await loadModel();
    const labels = await loadLabels();

    window.tf.enableProdMode();

    const history: ScanDetection[][] = [];

    let running = true;

    async function classifyFrame(): Promise<boolean> {

        if (!running) {
            return false;
        }

        const frame = await grabFrame(video);
        if (frame === null) {
            return new Promise(resolve =>
                requestAnimationFrame(async () => {
                    const result = await classifyFrame();
                    resolve(result);
                })
            );
        }

        const resultTensor = window.tf.tidy(() => {
            const imageTensor = window.tf.browser.fromPixels(frame);
            const inputTensor = window.tf.image.resizeBilinear(imageTensor, [224, 224]);
            const expandedTensor = window.tf.expandDims(inputTensor);
            const finalInputTensor = window.tf.cast(expandedTensor, 'float32');

            return model.predict(finalInputTensor) as Tensor<Rank>;
        })

        const probabilities: Int32Array = await resultTensor.data() as Int32Array;

        const output = new Map<string, number>();

        for (let i = 0; i < probabilities.length; i++) {
            const probability = probabilities[i] ;
            if (probability > 0) {
                output.set(labels[i], probability);
            }
        }

        const sorted = new Map([...output.entries()].sort((a, b) => b[1] - a[1]));
        const out: ScanDetection[] = Array.from(sorted.entries())
            .slice(0, 3)
            .map(([label, probability]): ScanDetection => ({ label, probability }));

        history.push(out);
        if (history.length > 1) {
            history.shift();
        }

        frames.push({
            frameData: frame,
            detections: out,
            createdAt: new Date(),
        });

        // TODO nyni posílame jenom posledních 20 framu
        if (frames.length > 20) {
            frames.shift();
        }

        if (frames.length >= 10 && history.length >= 1) {
            let found = true;
            for (const frameDetection of history) {
                let foundInFrame = false;
                for (const detection of frameDetection) {
                    if (detection.label === requiredLabel && detection.probability > requiredProbability) {
                        foundInFrame = true;
                    }
                }
                found = found && foundInFrame;
            }

            if (found) {
                return true;
            }
        }

        return new Promise(resolve => setTimeout(() => resolve(classifyFrame()), 200));
    }

    const result = await callWithTimeout(classifyFrame, 30 * 1000);

    return {
        result: result === true ? 'ok' : 'failed',
        frames,
    };
}

const grabFrame = async (video: HTMLVideoElement): Promise<ImageData|null> => {
    try {
        if (video.videoWidth === 0) {
            return null;
        }

        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        const ctx = canvas.getContext('2d');
        if (!ctx) return null;

        ctx.drawImage(video, 0, 0);

        return ctx.getImageData(0, 0, canvas.width, canvas.height);
    } catch (e) {
        console.error('Chyba při zachycení snímku:', e);
        return null;
    }
}

export {
    detectObjects
}
